import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Billy 28 Live Screen
 *
 * State Machine:
 * - Stream states: LIVE, NEVER_STARTED, STOPPED (from backend)
 * - Day counter: Only visible on/after launch date
 * - Viewer tracking: postMessage from Castr iframe with ?iframeMsg=true
 *
 * Castr manages paywall/auth - player iframe always visible.
 */

// Stream state constants
const STREAM_STATE = {
  LIVE: 'LIVE',
  NEVER_STARTED: 'NEVER_STARTED',
  STOPPED: 'STOPPED'
};

// Launch date from environment variable
const LAUNCH_DATE = new Date(process.env.REACT_APP_LAUNCH_DATE || '2026-02-27T13:30:00Z');

/**
 * Calculate day label - only returns value on/after launch date
 * Before launch: null (don't show)
 * On/after launch: "Day X of 28"
 * After day 28: "Show has ended"
 */
const calculateDayLabel = () => {
  const now = new Date();
  const timeDiff = now - LAUNCH_DATE;
  if (timeDiff < 0) return null;
  const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const day = daysDiff + 1;
  if (day > 28) return 'Show has ended';
  return `Day ${day} of 28`;
};

/**
 * Determine stream state from backend status
 */
const getStreamState = (streamStatus) => {
  if (!streamStatus) return null;

  if (streamStatus.isLive) {
    return STREAM_STATE.LIVE;
  }

  if (!streamStatus.hasEverBeenLive) {
    return STREAM_STATE.NEVER_STARTED;
  }

  return STREAM_STATE.STOPPED;
};

/**
 * Get message based on stream state
 */
const getStreamMessage = (streamState) => {
  switch (streamState) {
    case STREAM_STATE.NEVER_STARTED:
      return '28 days · $28 · One chance';
    case STREAM_STATE.STOPPED:
      return "Tech is being tech..\nThanks for being patient with us :)";
    case STREAM_STATE.LIVE:
    default:
      return null;
  }
};

const BillyLiveScreen = () => {
  // Stream status from backend
  const [streamStatus, setStreamStatus] = useState(null);
  const [dayLabel, setDayLabel] = useState(calculateDayLabel());

  // Viewer engagement tracking
  const [viewerState, setViewerState] = useState({
    pageLoaded: true,
    iframeLoaded: false,
    viewerHasWatched: false,
    totalWatchTime: 0
  });

  // Track whether Castr player is actively playing (recent watchTime update)
  const [castrPlaying, setCastrPlaying] = useState(false);
  const castrPlayingTimer = useRef(null);

  const iframeRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Player URL with iframeMsg=true for telemetry
  const basePlayerUrl = process.env.REACT_APP_CASTR_PROTECTED_PLAYER_URL ||
    process.env.REACT_APP_CASTR_PLAYER_URL ||
    'https://player.castr.com/live_676aa5b0ea6e11f088b75debb5eb63f6';

  // Add ?iframeMsg=true for postMessage telemetry and autoplay/muted params
  const CASTR_PLAYER_URL = basePlayerUrl.includes('?')
    ? `${basePlayerUrl}&iframeMsg=true&autoplay=on&muted=on&fullscreen=on`
    : `${basePlayerUrl}?iframeMsg=true&autoplay=on&muted=on&fullscreen=on`;

  // Compute derived state - suppress STOPPED message when Castr player is actively playing
  const streamState = getStreamState(streamStatus);
  const streamMessage = (streamState === STREAM_STATE.STOPPED && castrPlaying)
    ? null
    : getStreamMessage(streamState);

  // Fetch stream status from backend
  const fetchStreamStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/stream/status`);
      const data = await response.json();
      console.log('[Stream Status]', data);
      setStreamStatus(data);
    } catch (err) {
      console.error('Error fetching stream status:', err);
    }
  }, [API_URL]);

  // Update day label every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setDayLabel(calculateDayLabel());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Poll stream status every 15 seconds
  useEffect(() => {
    fetchStreamStatus();
    const interval = setInterval(fetchStreamStatus, 15000);
    return () => clearInterval(interval);
  }, [fetchStreamStatus]);

  // Listen for postMessage from Castr player iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Security: Only accept messages from Castr
      if (event.origin !== 'https://player.castr.com') {
        return;
      }

      try {
        // Parse message data
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        // Check for watchTime in the message
        if (data && typeof data.watchTime === 'number' && data.watchTime > 0) {
          setViewerState(prev => ({
            ...prev,
            viewerHasWatched: true,
            totalWatchTime: data.watchTime
          }));

          // Mark Castr as actively playing, reset after 30s of no updates
          setCastrPlaying(true);
          if (castrPlayingTimer.current) {
            clearTimeout(castrPlayingTimer.current);
          }
          castrPlayingTimer.current = setTimeout(() => {
            setCastrPlaying(false);
          }, 30000);
        }
      } catch (e) {
        // Ignore parse errors from other postMessages
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (castrPlayingTimer.current) {
        clearTimeout(castrPlayingTimer.current);
      }
    };
  }, []);

  // Handle iframe load
  const handleIframeLoad = () => {
    setViewerState(prev => ({
      ...prev,
      iframeLoaded: true
    }));
  };

  return (
    <div style={styles.container}>
      {/* Day Counter (top right) - only shown on/after launch date */}
      {dayLabel && (
        <div style={styles.topBar}>
          <div style={styles.dayCounter}>
            {dayLabel}
          </div>
        </div>
      )}

      {/* Stream State Message - shown when not live */}
      {streamMessage && (
        <div style={styles.centerNotification} className="center-notification">
          <div style={streamState === STREAM_STATE.NEVER_STARTED ? styles.neverStartedText : styles.notificationText}>
            {streamMessage}
          </div>
        </div>
      )}

      {/* Castr Player - always visible (handles paywall internally) */}
      <div style={styles.playerContainer}>
        <iframe
          ref={iframeRef}
          src={CASTR_PLAYER_URL}
          style={styles.player}
          frameBorder="0"
          scrolling="no"
          allow="autoplay; fullscreen; payment"
          allowFullScreen
          title="Billy 28 Live Stream"
          onLoad={handleIframeLoad}
        />
      </div>

      {/* Status Bar */}
      <div style={styles.statusBar}>
        <div style={styles.statusBarContent}>
          <div style={styles.statusInfo}>
            <span>
              Powered by <a href="https://FullAccess.Live" target="_blank" rel="noopener noreferrer" style={styles.statusLink}>FullAccess.Live</a>. All rights reserved. Contact us via - <a href="mailto:support@fullaccess.live" style={styles.statusLink}>support@fullaccess.live</a>
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @media (orientation: landscape) and (max-height: 500px) {
          .center-notification {
            bottom: 15% !important;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    position: 'relative',
    width: '100vw',
    height: '100vh',
    backgroundColor: '#000000',
    color: '#ffffff',
    fontFamily: 'Roboto, sans-serif',
    overflow: 'hidden'
  },
  topBar: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    zIndex: 10,
    pointerEvents: 'none'
  },
  dayCounter: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: 'bold',
    backdropFilter: 'blur(10px)',
    pointerEvents: 'auto'
  },
  centerNotification: {
    position: 'absolute',
    bottom: '10%',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 15,
    pointerEvents: 'none',
    width: '90%',
    maxWidth: '500px',
    textAlign: 'center'
  },
  notificationText: {
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    color: '#ffffff',
    padding: '12px 20px',
    borderRadius: '12px',
    fontSize: 'clamp(14px, 3.5vw, 18px)',
    fontWeight: '500',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    whiteSpace: 'pre-line'
  },
  neverStartedText: {
    color: '#454751',
    fontSize: 'clamp(14px, 4.2vw, 18px)',
    fontWeight: '500',
    fontFamily: "'Roboto', sans-serif"
  },
  playerContainer: {
    position: 'relative',
    width: '100%',
    height: 'calc(100% - 50px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  player: {
    width: '100%',
    height: '100%',
    border: 'none'
  },
  statusBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '50px',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    padding: '0 20px',
    zIndex: 10
  },
  statusBarContent: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    animation: 'pulse 2s infinite'
  },
  statusText: {
    fontSize: '13px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  },
  statusInfo: {
    fontSize: '14px',
    color: '#888'
  },
  statusLink: {
    color: '#888',
    textDecoration: 'underline'
  }
};

export default BillyLiveScreen;
